import { useEffect, useState } from 'react';

/**
 * Custom hook component for calculating a media query Boolean
 *
 * @param {String} query Media query
 *
 * @usage
 * const matches = useMediaQuery('(min-width: 768px)')
 *
 * @returns Boolean
 *
 * @source https://usehooks-ts.com/react-hook/use-media-query
 */
// eslint-disable-next-line import/prefer-default-export
export function useMediaQuery(query) {
    const getMatches = (query) => {
        // Prevents SSR issues
        if (typeof window !== 'undefined') {
            return window.matchMedia(query).matches;
        }
        return false;
    };

    const [matches, setMatches] = useState(getMatches(query));

    function handleChange() {
        setMatches(getMatches(query));
    }

    useEffect(() => {
        const matchMedia = window.matchMedia(query);

        // Triggered at the first client-side load and if query changes
        handleChange();

        // Listen matchMedia
        if (matchMedia.addListener) {
            matchMedia.addListener(handleChange);
        } else {
            matchMedia.addEventListener('change', handleChange);
        }

        return () => {
            if (matchMedia.removeListener) {
                matchMedia.removeListener(handleChange);
            } else {
                matchMedia.removeEventListener('change', handleChange);
            }
        };
    }, [query]);

    return matches;
}
